import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import './HastaEFatura.css';

// BANKO MODÜLÜ — HASTA e-FATURA (kullanıcı talebi: 2026-09-21).
// VB6 karşılığı: Banko_VB6\ESMM_H_NEW.frm.
//
// Hasta payı faturaları (REFERANS.NE=1) listelenir; gönderilmemiş olanlar seçilip
// e-fatura/e-SMM servisine gönderilir. Gönderim sonucu aynı kayda yazılır:
// FATURA_YOLLANDI, EFATNO (belge no), FATURA_ID (ETTN), FATURA_MSG (yanıt metni).
//
// DİKKAT: Gönderim GERÇEK mali belge üretir. Liste ve önizleme servise hiç dokunmaz;
// yalnızca "GÖNDER" düğmesi, onay penceresinden sonra istek atar.

const para = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const trTarih = (d) => (d ? `${String(d).slice(8, 10)}.${String(d).slice(5, 7)}.${String(d).slice(0, 4)}` : '');
const isoGun = (fark) => {
  const t = new Date();
  t.setDate(t.getDate() + fark);
  return t.toISOString().slice(0, 10);
};

export default function HastaEFatura() {
  const [bas, setBas] = useState(isoGun(-30));
  const [bit, setBit] = useState(isoGun(0));
  const [durum, setDurum] = useState('bekleyen');
  const [hastaFiltre, setHastaFiltre] = useState('');
  const [veri, setVeri] = useState(null);
  const [secililer, setSecililer] = useState([]);
  const [detay, setDetay] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');

  useEffect(() => {
    if (!bilgi) return undefined;
    const z = setTimeout(() => setBilgi(''), 8000);
    return () => clearTimeout(z);
  }, [bilgi]);

  const listele = useCallback(async () => {
    setYukleniyor(true); setHata(''); setSecililer([]);
    try {
      const { data } = await api.get('/banko/efatura/liste', {
        params: { bas, bit, durum, hasta: hastaFiltre.trim() || undefined },
      });
      setVeri(data);
      if (data.sinirAsildi) setBilgi('Çok fazla kayıt var; ilk 1000 satır gösteriliyor, tarih aralığını daraltınız.');
    } catch (err) {
      setHata(err.response?.data?.hata || 'Fatura listesi alınamadı.');
      setVeri(null);
    } finally {
      setYukleniyor(false);
    }
  }, [bas, bit, durum, hastaFiltre]);

  useEffect(() => { listele(); }, [listele]);

  const detayAc = async (s) => {
    setHata('');
    try {
      const { data } = await api.get(`/banko/efatura/${s.protNo}`);
      setDetay(data);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Fatura detayı alınamadı.');
    }
  };

  const satirlar = veri?.satirlar || [];
  const secilebilir = satirlar.filter(s => !s.gonderildi && s.genelToplam > 0);
  const hepsiSecili = secilebilir.length > 0 && secilebilir.every(s => secililer.includes(s.protNo));

  const tersCevir = (protNo) =>
    setSecililer(s => (s.includes(protNo) ? s.filter(x => x !== protNo) : [...s, protNo]));

  const excelAktar = async () => {
    if (satirlar.length === 0) { setHata('Listeniz boş!'); return; }
    setBilgi('Excel hazırlanıyor...');
    const ExcelJS = (await import('exceljs')).default;
    const kitap = new ExcelJS.Workbook();
    kitap.creator = 'FizyofiPlus';
    const sayfa = kitap.addWorksheet('Hasta e-Fatura', { views: [{ state: 'frozen', ySplit: 3 }] });
    const sutun = [
      { header: 'Tarih', key: 'tar', width: 12 },
      { header: 'Protokol', key: 'protNo', width: 11 },
      { header: 'Hasta No', key: 'kayitNo', width: 11 },
      { header: 'Hasta Adı', key: 'adSoy', width: 32 },
      { header: 'TC Kimlik', key: 'tc', width: 15 },
      { header: 'Matrah', key: 'toplam', width: 14 },
      { header: 'KDV', key: 'kdv', width: 12 },
      { header: 'Genel Toplam', key: 'gtop', width: 15 },
      { header: 'Durum', key: 'durum', width: 14 },
      { header: 'Belge No', key: 'efatNo', width: 20 },
      { header: 'ETTN', key: 'ettn', width: 38 },
      { header: 'Yanıt', key: 'mesaj', width: 30 },
    ];
    sayfa.columns = sutun;
    sayfa.spliceRows(1, 0, [], []);
    sayfa.getCell('A1').value = 'FizyofiPlus · Hasta e-Fatura Listesi';
    sayfa.getCell('A1').font = { size: 14, bold: true, color: { argb: 'FF1F2F46' } };
    sayfa.getCell('A2').value =
      `${trTarih(bas)} – ${trTarih(bit)} · ${veri.toplam} fatura · ${veri.gonderilen} gönderilmiş · `
      + `${veri.bekleyen} bekliyor · toplam ${para(veri.toplamTutar)} TL`;
    sayfa.getCell('A2').font = { size: 10, color: { argb: 'FF6B7A8C' } };
    const bs = sayfa.getRow(3);
    sutun.forEach((s, i) => { bs.getCell(i + 1).value = s.header; });
    bs.eachCell(c => {
      c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2F46' } };
    });
    satirlar.forEach((s, i) => {
      const r = sayfa.addRow({
        tar: trTarih(s.tar), protNo: s.protNo, kayitNo: s.kayitNo, adSoy: s.adSoy, tc: s.tcKimlik,
        toplam: s.toplam, kdv: s.kdv, gtop: s.genelToplam,
        durum: s.gonderildi ? 'Gönderildi' : 'Bekliyor',
        efatNo: s.eFatNo, ettn: s.faturaId, mesaj: s.mesaj,
      });
      r.getCell('E').numFmt = '@';
      ['F', 'G', 'H'].forEach(k => { r.getCell(k).numFmt = '#,##0.00'; });
      if (i % 2) r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F6FA' } }; });
    });
    sayfa.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: sutun.length } };
    const dosya = await kitap.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([dosya], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `hasta-efatura-${bas}_${bit}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setBilgi(`${satirlar.length} fatura Excel'e aktarıldı.`);
  };

  return (
    <div className="hef">
      <div className="hef-serit">
        <h2 className="hef-baslik">🧾 Hasta e-Fatura <span>· Banko Modülü</span></h2>
        <div className={`hef-durum ${hata ? 'hata' : bilgi ? 'bilgi' : ''}`}>
          {hata || bilgi || 'Gönderim gerçek mali belge üretir; listeleme ve önizleme servise dokunmaz.'}
        </div>
      </div>

      <div className="hef-filtre">
        <label><span>Başlangıç</span><input type="date" value={bas} onChange={e => setBas(e.target.value)} /></label>
        <label><span>Bitiş</span><input type="date" value={bit} onChange={e => setBit(e.target.value)} /></label>
        <div className="hef-secim">
          {[['bekleyen', 'Gönderilmemiş'], ['gonderilen', 'Gönderilmiş'], ['tumu', 'Tümü']].map(([k, ad]) => (
            <button key={k} type="button" className={`hef-sec ${durum === k ? 'aktif' : ''}`}
              onClick={() => setDurum(k)}>{ad}</button>
          ))}
        </div>
        <label className="hef-ara"><span>Hasta</span>
          <input value={hastaFiltre} onChange={e => setHastaFiltre(e.target.value)}
            placeholder="Ad soyad ya da TC" /></label>
        <button type="button" className="btn btn-secondary" onClick={listele} disabled={yukleniyor}>🔄 Yenile</button>
        <button type="button" className="btn btn-secondary" onClick={excelAktar}
          disabled={satirlar.length === 0}>📊 Excel</button>
      </div>

      {veri && (
        <div className="hef-ozet">
          <span><b>{veri.toplam}</b> fatura</span>
          <span className="yesil"><b>{veri.gonderilen}</b> gönderilmiş</span>
          <span className="mavi"><b>{veri.bekleyen}</b> bekliyor</span>
          <span><b>{para(veri.toplamTutar)}</b> TL toplam</span>
          <span className="secim"><b>{secililer.length}</b> seçili</span>
        </div>
      )}

      {yukleniyor && <div className="loading">Yükleniyor...</div>}

      {!yukleniyor && veri && (
        <div className="hef-liste card">
          <table>
            <thead>
              <tr>
                <th className="hef-sec-sutun">
                  <input type="checkbox" checked={hepsiSecili} disabled={secilebilir.length === 0}
                    onChange={() => setSecililer(hepsiSecili ? [] : secilebilir.map(s => s.protNo))} />
                </th>
                <th>Tarih</th><th>Protokol</th><th>Hasta Adı</th><th>TC Kimlik</th>
                <th className="sag">Matrah</th><th className="sag">KDV</th><th className="sag">Genel Toplam</th>
                <th>Durum</th><th>Belge No</th><th>Yanıt</th><th />
              </tr>
            </thead>
            <tbody>
              {satirlar.length === 0 && (
                <tr><td colSpan={12} className="hef-bos">Bu kriterlerde fatura yok.</td></tr>
              )}
              {satirlar.map(s => (
                <tr key={s.protNo} className={s.gonderildi ? 'gonderilmis' : ''}>
                  <td className="hef-sec-sutun">
                    <input type="checkbox" checked={secililer.includes(s.protNo)}
                      disabled={s.gonderildi || s.genelToplam <= 0}
                      onChange={() => tersCevir(s.protNo)} />
                  </td>
                  <td>{trTarih(s.tar)}</td>
                  <td>{s.protNo}</td>
                  <td className="hef-ad">{s.adSoy}</td>
                  <td>{s.tcKimlik || '—'}</td>
                  <td className="sag">{para(s.toplam)}</td>
                  <td className="sag">{para(s.kdv)}</td>
                  <td className="sag"><b>{para(s.genelToplam)}</b></td>
                  <td>
                    {s.gonderildi
                      ? <span className="hef-rozet yesil">Gönderildi</span>
                      : <span className="hef-rozet mavi">Bekliyor</span>}
                  </td>
                  <td>{s.eFatNo || '—'}</td>
                  <td className="hef-ad" title={s.faturaId}>{s.mesaj || '—'}</td>
                  <td>
                    <button type="button" className="hef-detay" onClick={() => detayAc(s)}>İncele</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detay && (
        <div className="hef-modal-zemin" onClick={() => setDetay(null)} role="presentation">
          <div className="hef-modal" onClick={e => e.stopPropagation()} role="presentation">
            <div className="hef-modal-bas">
              <strong>Fatura {detay.fatura.protNo} — {detay.fatura.adSoy}</strong>
              <button type="button" onClick={() => setDetay(null)}>✕</button>
            </div>
            <div className="hef-modal-govde">
              <div className="hef-bilgi">
                <div><span>Tarih</span><b>{trTarih(detay.fatura.tar)} {detay.fatura.saat}</b></div>
                <div><span>TC Kimlik</span><b>{detay.fatura.tcKimlik || '—'}</b></div>
                <div><span>Adres</span><b>{detay.adres || '—'}</b></div>
                <div><span>İlçe / İl</span><b>{[detay.ilce, detay.il].filter(Boolean).join(' / ') || '—'}</b></div>
                <div><span>E-posta</span><b>{detay.email || '—'}</b></div>
                <div><span>Telefon</span><b>{detay.telefon || '—'}</b></div>
                {detay.fatura.eFatNo && <div><span>Belge No</span><b>{detay.fatura.eFatNo}</b></div>}
                {detay.fatura.faturaId && <div><span>ETTN</span><b className="hef-ettn">{detay.fatura.faturaId}</b></div>}
              </div>

              <table className="hef-kalem">
                <thead><tr><th>Hizmet</th><th className="sag">Adet</th><th className="sag">B.Fiyat</th><th className="sag">Matrah</th><th className="sag">KDV %</th><th className="sag">KDV'li</th></tr></thead>
                <tbody>
                  {detay.kalemler.length === 0 && <tr><td colSpan={6} className="hef-bos">Hizmet satırı yok.</td></tr>}
                  {detay.kalemler.map((k, i) => (
                    <tr key={i}>
                      <td>{k.hizmet}</td>
                      <td className="sag">{k.adet}</td>
                      <td className="sag">{para(k.birimFiyat)}</td>
                      <td className="sag">{para(k.tutar)}</td>
                      <td className="sag">{k.kdvOrani}</td>
                      <td className="sag">{para(k.kdvliTutar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="hef-toplamlar">
                <div><span>Matrah</span><b>{para(detay.fatura.toplam)}</b></div>
                <div><span>KDV</span><b>{para(detay.fatura.kdv)}</b></div>
                <div className="genel"><span>GENEL TOPLAM</span><b>{para(detay.fatura.genelToplam)}</b></div>
              </div>

              {detay.eksikler.length > 0 && (
                <div className="hef-eksik">
                  <strong>Gönderime engel / eksik bilgiler:</strong>
                  <ul>{detay.eksikler.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
